import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { Assessment } from '../../../types/AssessmentTypes';
import axios from '../../../../services/ApiService';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import DeleteIcon from '@mui/icons-material/Delete';
import { Helmet } from 'react-helmet-async';
import { BVTConstants } from '../../../../utils/BVTConstants';
import Swal from 'sweetalert2';

export default function AssessmentForm() {
  const [assessmentData, setAssessmentData] = useState<Assessment>({
    id: 0,
    name: '',
    description: '',
    totalScore: 0,
    status: '',
    createdAt: '',
    updatedAt: '',
    subCategories: [],
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [subCategoryIdCounter, setSubCategoryIdCounter] = useState<number>(1);

  useEffect(() => {
    if (id) {
      fetchAssessmentData(id);
    }
  }, [id]);

  const fetchAssessmentData = async (assessmentId: number) => {
    try {
      setLoading(true);
      const response = await axios.post("/getAssessment", assessmentId);
      setAssessmentData(response.data);
    } catch (error) {
      console.error('Error fetching assessment data.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubCategory = () => {
    const newSubCategory = {
      id: 0,
      seqId: subCategoryIdCounter,
      title: '',
      questions: [],
      questionIdCounter: 1,
    };
    setAssessmentData((prev) => ({
      ...prev,
      subCategories: [...prev.subCategories, newSubCategory],
    }));
    setSubCategoryIdCounter((prev) => prev + 1);
  };

  const handleDeleteSubCategory = (seqId: number) => {
    setAssessmentData((prev) => {
      const updatedSubCategories = prev.subCategories
        .filter((sc) => sc.seqId !== seqId)
        .map((sc, index) => ({ ...sc, seqId: index + 1 }));
      return { ...prev, subCategories: updatedSubCategories };
    });
    setSubCategoryIdCounter((prev) => prev - 1);
  };

  const handleAddQuestion = (seqId: number) => {
    const newQuestion = {
      id: 0,
      seqId: getQuestionIdForSubCategory(seqId),
      text: '',
      options: [],
      helpTip: '',
      isLink: false,
    };

    setAssessmentData((prev) => {
      const updatedSubCategories = prev.subCategories.map((sc) =>
        sc.seqId === seqId
          ? { ...sc, questions: [...sc.questions, newQuestion] }
          : sc
      );
      return { ...prev, subCategories: updatedSubCategories };
    });
  };

  const getQuestionIdForSubCategory = (seqId: number) => {
    const subCategory = assessmentData.subCategories.find(sc => sc.seqId === seqId);
    if (subCategory) {
      const newQuestionId = subCategory.questions.length + 1;
      return newQuestionId;
    }
    return 1;
  };

  const handleDeleteQuestion = (seqId: number, questionSeqId: number) => {
    setAssessmentData((prev) => {
      const updatedSubCategories = prev.subCategories.map((sc) => {
        if (sc.seqId === seqId) {
          const updatedQuestions = sc.questions
            .filter((q) => q.seqId !== questionSeqId)
            .map((q, index) => ({ ...q, seqId: index + 1 }));
          return { ...sc, questions: updatedQuestions };
        }
        return sc;
      });
  
      return { ...prev, subCategories: updatedSubCategories };
    });
  };

  const handleAddOption = (seqId: number, questionSeqId: number) => {
    const newOption = { seqId: getOptionIdForQuestion(seqId, questionSeqId), label: '', score: 0, id: 0 };
    setAssessmentData((prev) => {
      const updatedSubCategories = prev.subCategories.map((sc) =>
        sc.seqId === seqId
          ? {
              ...sc,
              questions: sc.questions.map((q) =>
                q.seqId === questionSeqId ? { ...q, options: [...q.options, newOption] } : q
              ),
            }
          : sc
      );
      return { ...prev, subCategories: updatedSubCategories };
    });
  };

  const getOptionIdForQuestion = (seqId: number, questionSeqId: number) => {
    const subCategory = assessmentData.subCategories.find(sc => sc.seqId === seqId);
    const question = subCategory?.questions.find(q => q.seqId === questionSeqId);
    if (question) {
      const newOptionId = question.options.length + 1;
      return newOptionId;
    }
    return 1;
  };

  const handleDeleteOption = (seqId: number, questionSeqId: number, optionSeqId: number) => {
    setAssessmentData((prev) => {
      const updatedSubCategories = prev.subCategories.map((sc) => {
        if (sc.seqId === seqId) {
          const updatedQuestions = sc.questions.map((q) => {
            if (q.seqId === questionSeqId) {
              const updatedOptions = q.options
                .filter((o) => o.seqId !== optionSeqId)
                .map((o, index) => ({ ...o, seqId: index + 1 }));
              return { ...q, options: updatedOptions };
            }
            return q;
          });
          return { ...sc, questions: updatedQuestions };
        }
        return sc;
      });
  
      return { ...prev, subCategories: updatedSubCategories };
    });
  };

  const calculateTotalScore = () => {
    return assessmentData.subCategories.reduce((total, sc) => {
      return (
        total +
        sc.questions.reduce((qTotal, question) => {
          const maxOptionScore = Math.max(...question.options.map((o) => o.score), 0);
          return qTotal + maxOptionScore;
        }, 0)
      );
    }, 0);
  };

  const handleSaveAssessment = async (isDraft: boolean = false) => {
    const errors: any = {};
    
    // Always validate name and description for both draft and submit
    if (!assessmentData.name.trim()) errors.name = "Assessment name is required.";
    if (!assessmentData.description.trim()) errors.description = "Description is required.";

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    const assessmentToSave = {
      ...assessmentData,
      totalScore: calculateTotalScore(),
      status: isDraft ? 'DRAFT' : BVTConstants.ACTIVE,
    };

    console.log('Saving Assessment:', assessmentToSave);
    setLoading(true);
    try {
      if (id) {
        await axios.post("/updateAssessment", assessmentToSave);
        console.log('Assessment updated successfully');
        toast.success(isDraft ? "Assessment Draft Updated Successfully." : "Assessment Updated Successfully.");
      } else {
        await axios.post("/createAssessment", assessmentToSave);
        console.log('Assessment saved successfully');
        toast.success(isDraft ? "Assessment Draft Saved Successfully." : "Assessment Saved Successfully.");
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving assessment.');
      toast.error('Error saving assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAssessment = async () => {
    // Validate first before showing confirmation dialog
    const errors: any = {};
    
    if (!assessmentData.name.trim()) errors.name = "Assessment name is required.";
    if (!assessmentData.description.trim()) errors.description = "Description is required.";

    if (assessmentData.subCategories.length === 0) {
      errors.subCategory = "At least one subcategory is required.";
    }

    assessmentData.subCategories.forEach((subCategory, index) => {
      if (!subCategory.title) {
        errors[`subCategory_${index}_title`] = "Subcategory title is required.";
      }

      if (subCategory.questions.length === 0) {
        errors[`subCategory_${index}_questions`] = "Each subcategory must have at least one question.";
      }

      subCategory.questions.forEach((question, qIndex) => {
        if (!question.text) {
          errors[`question_${qIndex}_text`] = "Question text is required.";
        }

        if (question.options.length === 0) {
          errors[`question_${qIndex}_options`] = "Each question must have at least one option.";
        }

        question.options.forEach((option, oIndex) => {
          if (!option.label) {
            errors[`option_${oIndex}_label`] = "Option label is required.";
          }
          if (option.score < 0) {
            errors[`option_${oIndex}_score`] = "Option score must be 0 or more.";
          }
        });
      });
    });

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      toast.error('Please fill in all required fields.');
      return;
    }

    // If validation passes, show confirmation dialog
    const result = await Swal.fire({
      title: 'Submit Assessment?',
      text: 'Once submitted, this assessment cannot be edited. Are you sure you want to submit?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Submit!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await handleSaveAssessment(false);
    }
  };

  const handleSaveDraft = async () => {
    await handleSaveAssessment(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[field];
      return newErrors;
    });
    setAssessmentData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  return (
    <>
      <Helmet><title>BVT Admin - Assessment</title></Helmet>
              <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmitAssessment();
          }}
        >
        <Card sx={{ width: '100%' }}>
          <CardHeader
            title={`${id ? 'Update' : 'Create'} Assessment`}
            subheader={`Total Score: ${calculateTotalScore()}`}
          />
          <Divider />
          <CardContent>
            <Stack spacing={3} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth error={Boolean(errors.name)}>
                    <InputLabel>Assessment Name</InputLabel>
                    <OutlinedInput
                      label="Assessment Name"
                      value={assessmentData.name}
                      onChange={(e) => handleInputChange(e, 'name')}
                    />
                    {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={Boolean(errors.description)}>
                    <InputLabel>Description</InputLabel>
                    <OutlinedInput
                      label="Description"
                      value={assessmentData.description}
                      onChange={(e) => handleInputChange(e, 'description')}
                    />
                    {errors.description && <FormHelperText>{errors.description}</FormHelperText>}
                  </FormControl>
                </Grid>
              </Grid>

              <Card sx={{ marginTop: 2 }}>
                <CardHeader title="Subcategories" />
                <Divider />
                <CardContent>
                  {assessmentData.subCategories.map((subCategory, index) => (
                    <Box key={subCategory.seqId} sx={{ mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={10}>
                          <FormControl fullWidth error={Boolean(errors[`subCategory_${index}_title`])}>
                            <InputLabel>Subcategory</InputLabel>
                            <OutlinedInput
                              label="Subcategory"
                              value={subCategory.title}
                              onChange={(e) => {
                                const updatedSubCategories = assessmentData.subCategories.map((sc) =>
                                  sc.seqId === subCategory.seqId
                                    ? { ...sc, title: e.target.value }
                                    : sc
                                );
                                setAssessmentData({ ...assessmentData, subCategories: updatedSubCategories });
                              }}
                            />
                            {errors[`subCategory_${index}_title`] && <FormHelperText>{errors[`subCategory_${index}_title`]}</FormHelperText>}
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteSubCategory(subCategory.seqId)}
                            sx={{ height: '100%' }}
                          >
                            <DeleteIcon />
                          </Button>
                        </Grid>
                      </Grid>

                      <Card sx={{ marginTop: 2 }}>
                        <CardHeader title="Questions" />
                        <Divider />
                        <CardContent>
                          {subCategory.questions.map((question, qIndex) => (
                            <Box key={question.seqId} sx={{ mt: 2 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={10}>
                                  <FormControl fullWidth error={Boolean(errors[`question_${qIndex}_text`])}>
                                    <InputLabel>Question</InputLabel>
                                    <OutlinedInput
                                      label="Question"
                                      value={question.text}
                                      onChange={(e) => {
                                        const updatedQuestions = subCategory.questions.map((q) =>
                                          q.seqId === question.seqId ? { ...q, text: e.target.value } : q
                                        );
                                        const updatedSubCategories = assessmentData.subCategories.map((sc) =>
                                          sc.seqId === subCategory.seqId ? { ...sc, questions: updatedQuestions } : sc
                                        );
                                        setAssessmentData({ ...assessmentData, subCategories: updatedSubCategories });
                                      }}
                                    />
                                    {errors[`question_${qIndex}_text`] && <FormHelperText>{errors[`question_${qIndex}_text`]}</FormHelperText>}
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleDeleteQuestion(subCategory.seqId, question.seqId)}
                                    sx={{ height: '100%' }}
                                  >
                                    <DeleteIcon />
                                  </Button>
                                </Grid>
                              </Grid>

                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={10}>
                                  <FormControl fullWidth>
                                    <InputLabel>Help Tip</InputLabel>
                                    <OutlinedInput
                                      label="Help Tip"
                                      value={question.helpTip}
                                      onChange={(e) => {
                                        const updatedQuestions = subCategory.questions.map((q) =>
                                          q.seqId === question.seqId ? { ...q, helpTip: e.target.value } : q
                                        );
                                        const updatedSubCategories = assessmentData.subCategories.map((sc) =>
                                          sc.seqId === subCategory.seqId ? { ...sc, questions: updatedQuestions } : sc
                                        );
                                        setAssessmentData({ ...assessmentData, subCategories: updatedSubCategories });
                                      }}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid item xs={2}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={question.isLink}
                                        onChange={(e) => {
                                          const updatedQuestions = subCategory.questions.map((q) =>
                                            q.seqId === question.seqId ? { ...q, isLink: e.target.checked } : q
                                          );
                                          const updatedSubCategories = assessmentData.subCategories.map((sc) =>
                                            sc.seqId === subCategory.seqId ? { ...sc, questions: updatedQuestions } : sc
                                          );
                                          setAssessmentData({ ...assessmentData, subCategories: updatedSubCategories });
                                        }}
                                      />
                                    }
                                    label="Is Link"
                                  />
                                </Grid>
                              </Grid>

                              <Card sx={{ marginTop: 2 }}>
                                <CardHeader title="Options" />
                                <Divider />
                                <CardContent>
                                  {question.options.map((option, oIndex) => (
                                    <Grid container spacing={2} key={option.seqId} sx={{ mt: 1 }}>
                                      <Grid item xs={5}>
                                        <FormControl fullWidth error={Boolean(errors[`option_${oIndex}_label`])}>
                                          <InputLabel>Option</InputLabel>
                                          <OutlinedInput
                                            label="Option"
                                            value={option.label}
                                            onChange={(e) => {
                                              const newOptions = question.options.map((opt) =>
                                                opt.seqId === option.seqId ? { ...opt, label: e.target.value } : opt
                                              );
                                              const newQuestions = subCategory.questions.map((q) =>
                                                q.seqId === question.seqId ? { ...q, options: newOptions } : q
                                              );
                                              const newSubCategories = assessmentData.subCategories.map((sc) =>
                                                sc.seqId === subCategory.seqId ? { ...sc, questions: newQuestions } : sc
                                              );
                                              setAssessmentData({
                                                ...assessmentData,
                                                subCategories: newSubCategories,
                                              });
                                            }}
                                          />
                                          {errors[`option_${oIndex}_label`] && <FormHelperText>{errors[`option_${oIndex}_label`]}</FormHelperText>}
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={5}>
                                        <FormControl fullWidth error={Boolean(errors[`option_${oIndex}_score`])}>
                                          <InputLabel>Score</InputLabel>
                                          <OutlinedInput
                                            label="Score"
                                            type="number"
                                            value={option.score} // Changed marks to score
                                            onChange={(e) => {
                                              const newOptions = question.options.map((opt) =>
                                                opt.seqId === option.seqId ? { ...opt, score: parseFloat(e.target.value) } : opt
                                              );
                                              const newQuestions = subCategory.questions.map((q) =>
                                                q.seqId === question.seqId ? { ...q, options: newOptions } : q
                                              );
                                              const newSubCategories = assessmentData.subCategories.map((sc) =>
                                                sc.seqId === subCategory.seqId ? { ...sc, questions: newQuestions } : sc
                                              );
                                              setAssessmentData({
                                                ...assessmentData,
                                                subCategories: newSubCategories,
                                              });
                                            }}
                                          />
                                          {errors[`option_${oIndex}_score`] && <FormHelperText>{errors[`option_${oIndex}_score`]}</FormHelperText>}
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={2}>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          onClick={() => handleDeleteOption(subCategory.seqId, question.seqId, option.seqId)}
                                          sx={{ height: '100%' }}
                                        >
                                          <DeleteIcon />
                                        </Button>
                                      </Grid>
                                    </Grid>
                                  ))}

                                  <Button
                                    variant="outlined"
                                    onClick={() => handleAddOption(subCategory.seqId, question.seqId)}
                                    sx={{ marginTop: '10px' }}
                                  >
                                    Add Option
                                  </Button>
                                </CardContent>
                              </Card>
                            </Box>
                          ))}

                          <Button
                            variant="outlined"
                            onClick={() => handleAddQuestion(subCategory.seqId)}
                            sx={{ marginTop: '10px' }}
                          >
                            Add Question
                          </Button>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}

                  <Button variant="outlined" onClick={handleAddSubCategory}>
                    Add Subcategory
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="outlined" onClick={handleSaveDraft}>
              Save Draft
            </Button>
            <Button variant="contained" onClick={handleSubmitAssessment}>
              Submit Assessment
            </Button>
          </CardActions>
        </Card>
        <LoadingSpinner loading={loading} />
      </form>
    </>
  );
}
